<!-- 通用上传卡片：空态为虚线引导框，有图时显示预览 + 重新上传/删除
     只负责 UI 与选文件，真正的上传逻辑由父组件的 chooseImg 处理
     注意：内部一律绝对定位填充，保证选中图片前后卡片尺寸完全一致 -->
<template>
    <div class="upload-card" :class="[shape, { 'is-disabled': loading, small }]"
        :style="{ width: boxW, height: boxH }">

        <el-upload action="" :auto-upload="false" :show-file-list="false" :accept="accept"
            :on-change="handleChange">

            <!-- 已上传：预览图 + 悬浮操作 -->
            <div v-if="preview" class="preview-box">
                <el-image :src="preview" fit="cover" class="preview-img" />
                <div class="mask">
                    <span>重新上传</span>
                </div>
                <!-- 删除 -->
                <div class="corner-btn btn-del" title="删除" @click.stop="handleRemove">
                    <i class="el-icon-close"></i>
                </div>
                <!-- 放大预览 -->
                <div class="corner-btn btn-zoom" title="放大预览" @click.stop="openViewer">
                    <i class="el-icon-zoom-in"></i>
                </div>
            </div>

            <!-- 空态：虚线引导（纯文字，无图标） -->
            <div v-else class="empty-box">
                <p class="u-title">{{ title }}</p>
                <p v-if="tip" class="u-tip">{{ tip }}</p>
            </div>
        </el-upload>

        <!-- 上传中遮罩：只在当前卡片内，不触发整页 loading -->
        <div v-if="loading" class="loading-mask">
            <i class="el-icon-loading"></i>
            <span>上传中</span>
        </div>

        <!-- 大图预览（挂到 body，避免被父级裁剪） -->
        <el-dialog :visible.sync="viewerVisible" :append-to-body="true" :title="title || '图片预览'"
            width="720px" top="6vh" custom-class="img-viewer-dialog" @closed="resetZoom">

            <div class="viewer-body" @wheel.prevent="onWheel">
                <img :src="preview" class="viewer-img" :style="{ width: (scale * 100) + '%' }" />
            </div>

            <template #footer>
                <div class="viewer-footer">
                    <el-button size="mini" icon="el-icon-zoom-out" :disabled="scale <= minScale"
                        @click="zoomOut">缩小</el-button>
                    <span class="scale-text">{{ Math.round(scale * 100) }}%</span>
                    <el-button size="mini" icon="el-icon-zoom-in" :disabled="scale >= maxScale"
                        @click="zoomIn">放大</el-button>
                    <el-button size="mini" @click="resetZoom">重置</el-button>
                </div>
            </template>
        </el-dialog>
    </div>
</template>

<script>
export default {
    name: 'UploadCard',
    props: {
        // 本地预览地址（blob）
        preview: { type: String, default: '' },
        // 空态标题，如「身份证正面」
        title: { type: String, default: '上传图片' },
        // 空态提示，如「支持JPG、PNG格式」
        tip: { type: String, default: '' },
        // rect-方形（证件/证照）  circle-圆形（头像）
        shape: { type: String, default: 'rect' },
        width: { type: [Number, String], default: 140 },
        height: { type: [Number, String], default: 160 },
        loading: { type: Boolean, default: false },
        accept: { type: String, default: 'image/jpeg,image/png,image/jpg' },
        // 小尺寸模式（一排多张证照时用）
        small: { type: Boolean, default: false },
    },
    data() {
        return {
            viewerVisible: false,   // 大图预览弹窗
            scale: 1,               // 当前缩放比例
            minScale: 0.2,
            maxScale: 4,
        };
    },
    computed: {
        boxW() {
            return typeof this.width === 'number' ? this.width + 'px' : this.width
        },
        boxH() {
            return typeof this.height === 'number' ? this.height + 'px' : this.height
        },
    },
    methods: {
        handleChange(e) {
            this.$emit('change', e)
        },
        handleRemove() {
            this.$emit('remove')
        },

        // ===== 大图预览 =====
        openViewer() {
            this.scale = 1
            this.viewerVisible = true
        },
        zoomIn() {
            this.scale = Math.min(this.maxScale, +(this.scale + 0.25).toFixed(2))
        },
        zoomOut() {
            this.scale = Math.max(this.minScale, +(this.scale - 0.25).toFixed(2))
        },
        resetZoom() {
            this.scale = 1
        },
        // 滚轮缩放：向上放大，向下缩小
        onWheel(e) {
            if (e.deltaY < 0) {
                this.zoomIn()
            } else {
                this.zoomOut()
            }
        },
    }
};
</script>

<style lang="scss" scoped>
.upload-card {
    position: relative;
    display: inline-block;
    vertical-align: top;
    overflow: hidden;
    border-radius: 10px;

    // el-upload 绝对定位铺满，内部内容不会反向撑开卡片
    ::v-deep .el-upload {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        display: block;
        width: 100%;
        height: 100%;
    }

    &.is-disabled {
        pointer-events: none;
    }

    // ===== 空态 =====
    .empty-box {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        box-sizing: border-box;
        border: 1px dashed #c8d3e0;
        border-radius: 10px;
        background: #fafcff;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        padding: 0 6px;
        text-align: center;
        cursor: pointer;
        transition: border-color 0.25s ease, background 0.25s ease, box-shadow 0.25s ease;

        .u-title {
            margin: 0;
            font-size: 14px;
            line-height: 1.5;
            color: #5a6b80;
            font-weight: 500;
            transition: color 0.25s ease;
        }

        .u-tip {
            margin: 6px 0 0;
            font-size: 11px;
            line-height: 1.4;
            color: #a8b4c4;
        }

        &:hover {
            border-color: #1565d8;
            background: #f2f8ff;
            box-shadow: 0 4px 14px rgba(21, 101, 216, 0.12);

            .u-title {
                color: #1565d8;
            }
        }
    }

    // ===== 预览态 =====
    .preview-box {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        box-sizing: border-box;
        border-radius: 10px;
        overflow: hidden;
        border: 1px solid #e2e8f0;
        background: #f5f7fa;
        cursor: pointer;

        .preview-img {
            width: 100%;
            height: 100%;
            display: block;

            ::v-deep .el-image__inner {
                width: 100%;
                height: 100%;
                object-fit: cover;
                display: block;
            }
        }

        // 悬浮遮罩
        .mask {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            background: rgba(21, 101, 216, 0.62);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.25s ease;

            span {
                font-size: 13px;
            }
        }

        &:hover .mask {
            opacity: 1;
        }

        // 角标按钮：右上删除、右下放大
        .corner-btn {
            position: absolute;
            width: 24px;
            height: 24px;
            border-radius: 50%;
            background: rgba(0, 0, 0, 0.55);
            color: #fff;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 13px;
            opacity: 0;
            transition: all 0.2s ease;
            z-index: 2;

            &:hover {
                transform: scale(1.12);
            }
        }

        .btn-del {
            top: 6px;
            right: 6px;

            &:hover {
                background: #f56c6c;
            }
        }

        .btn-zoom {
            bottom: 6px;
            right: 6px;

            &:hover {
                background: #1565d8;
            }
        }

        &:hover .corner-btn {
            opacity: 1;
        }
    }

    // ===== 圆形（头像） =====
    &.circle {
        border-radius: 50%;

        .empty-box,
        .preview-box {
            border-radius: 50%;
        }

        .empty-box {
            .u-title {
                font-size: 13px;
            }

            .u-tip {
                font-size: 10px;
            }
        }
    }

    // ===== 上传中 =====
    .loading-mask {
        position: absolute;
        top: 0;
        right: 0;
        bottom: 0;
        left: 0;
        border-radius: 10px;
        background: rgba(255, 255, 255, 0.78);
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 6px;
        color: #1565d8;
        font-size: 12px;

        i {
            font-size: 22px;
        }
    }

    &.circle .loading-mask {
        border-radius: 50%;
    }
}

// 小尺寸（一排多张证照）
.upload-card.small {
    .empty-box {
        .u-title {
            font-size: 13px;
        }

        .u-tip {
            font-size: 10px;
            margin-top: 4px;
        }
    }
}

// ===== 大图预览弹窗 =====
::v-deep .img-viewer-dialog {
    border-radius: 12px;
    overflow: hidden;

    .el-dialog__header {
        padding: 14px 20px;
        border-bottom: 1px solid #edf2f8;
        background: #f8fbff;

        .el-dialog__title {
            font-size: 15px;
            font-weight: 500;
            color: #2b3b52;
        }
    }

    .el-dialog__body {
        padding: 0;
        background: #f5f7fa;
    }

    .el-dialog__footer {
        padding: 12px 20px;
        border-top: 1px solid #edf2f8;
        background: #fff;
    }
}

.viewer-body {
    width: 100%;
    max-height: 66vh;
    overflow: auto;
    display: flex;
    justify-content: center;
    align-items: flex-start;
    background: #f5f7fa;

    .viewer-img {
        display: block;
        transition: width 0.18s ease;
    }
}

.viewer-footer {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;

    .scale-text {
        min-width: 52px;
        text-align: center;
        font-size: 13px;
        color: #5a6b80;
        font-variant-numeric: tabular-nums;
    }
}
</style>
